import React from "react";
import styled from "styled-components";
import spectrumLogo from "../pngs/SpectrumLogo.png";
import { colors, typography, spacing, media } from "../styles/designSystem";
import {
    PageHeadline,
    SectionHeadline,
    SubsectionHeadline,
    TerminalLabel,
} from "../components/Typography";
import InfoCard from "../components/sub-components/InfoCard";
import MetricsCard from "../components/sub-components/MetricsCard";
import TestimonialSection from "../components/TestimonialsSection";
import NDADisclosure from "../components/NDADisclosure";
import {
    AgentWorkflow,
    AppArchitecture,
    ComponentAnatomy,
    StateCoverage,
} from "../components/WinDiagrams";

/**
 * Spectrum WIN.
 *
 * Written as an engineering document rather than a design-process walkthrough: the
 * constraint, the model chosen to satisfy it, the contract that model imposed on
 * components, and the number it moved. Diagrams carry the architecture so the prose
 * does not have to describe shapes in words.
 */

const PageWrapper = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};
    color: ${({ theme }) => theme.text};

    ${media.md} {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Header = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.55);
    padding: ${spacing[800]};
    margin-bottom: ${spacing[900]};

    ${media.md} {
        padding: ${spacing[600]};
    }
`;

const Logo = styled.img`
    display: block;
    max-width: 220px;
    height: auto;
    filter: ${({ theme }) => theme.logoFilter};
    margin-bottom: ${spacing[700]};

    ${media.md} {
        max-width: 160px;
    }
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[300]} ${spacing[700]};
    margin-bottom: ${spacing[600]};
`;

const MetaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[100]};

    span:last-child {
        font-size: ${typography.fontSize[200]};
        color: ${({ theme }) => theme.text};
    }
`;

const Lede = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;
    max-width: 62ch;
`;

const Layout = styled.div`
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    gap: ${spacing[900]};
    align-items: start;

    ${media.lg} {
        grid-template-columns: 1fr;
        gap: ${spacing[700]};
    }
`;

const Sidebar = styled.aside`
    position: sticky;
    top: calc(var(--chrome-h, 145px) + ${spacing[600]});
    align-self: start;

    ${media.lg} {
        position: static;
    }
`;

const Body = styled.div`
    min-width: 0;
`;

const Section = styled.section`
    margin-bottom: ${spacing[1000]};
`;

const Para = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[500]};
    max-width: 68ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Bullets = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 ${spacing[600]};
    max-width: 68ch;
`;

const Bullet = styled.li`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    padding-left: ${spacing[600]};
    position: relative;
    margin-bottom: ${spacing[300]};

    &::before {
        content: "\\25B8";
        position: absolute;
        left: 0;
        color: ${({ theme }) => theme.primary};
    }
`;

// The workflow model, set apart because it is the decision the rest follows from.
const Model = styled.div`
    border: 1px solid ${({ theme }) => theme.primary};
    border-left-width: 2px;
    background: ${({ theme }) => theme.glowSoft};
    padding: ${spacing[600]};
    margin: ${spacing[700]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    line-height: ${typography.lineHeight[500]};
    word-break: break-word;
`;

const DiagramSlot = styled.div`
    margin: ${spacing[700]} 0;
`;

const SubHead = styled(SubsectionHeadline)`
    margin-top: ${spacing[700]};
`;

const Spectrum = () => (
    <PageWrapper>
        <Header>
            <Logo src={spectrumLogo} alt="Spectrum logo" />
            <PageHeadline>Spectrum WIN</PageHeadline>
            <Meta>
                <MetaItem>
                    <TerminalLabel>Role</TerminalLabel>
                    <span>Product Engineer</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Product</TerminalLabel>
                    <span>WIN &mdash; Internal Agent Platform</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Service Period</TerminalLabel>
                    <span>Jun 2021 &mdash; Jul 2025</span>
                </MetaItem>
            </Meta>
            <Lede>
                Support agents were navigating multiple systems to understand a customer&rsquo;s
                account, diagnose the problem, and decide what to do next. Every extra system,
                search, and context switch was time the customer spent on the phone. WIN brought
                those workflows into one contextual application, and average call handling fell from
                roughly <strong>five minutes to three</strong>.
            </Lede>
        </Header>

        <Layout>
            <Sidebar>
                <InfoCard
                    dates="Jun 2021 – Jul 2025"
                    roles={["Product Engineer", "Frontend Architecture", "Design Systems"]}
                    tools={["React", "Design Tokens", "WCAG", "Figma", "Jira"]}
                />
            </Sidebar>

            <Body>
                <Section>
                    <SectionHeadline>The Engineering Problem</SectionHeadline>
                    <Para>
                        The challenge was not designing a better dashboard. The application had to
                        work out what information mattered for a specific customer and a specific
                        support scenario, while staying predictable enough for agents who sat in it
                        for an entire shift.
                    </Para>
                    <Bullets>
                        <Bullet>Customer and account data could produce many different interface states.</Bullet>
                        <Bullet>Troubleshooting workflows had to change based on context.</Bullet>
                        <Bullet>Components had to behave consistently across multiple products and teams.</Bullet>
                        <Bullet>Loading, empty, error, disabled, and permission states all needed defined behavior.</Bullet>
                        <Bullet>Accessibility requirements were strict and non-negotiable.</Bullet>
                        <Bullet>New workflows had to be addable without one-off UI implementations.</Bullet>
                    </Bullets>
                    <Para>
                        So the real problem was building a{" "}
                        <strong>reusable frontend system capable of representing complex
                        operational workflows</strong> &mdash; not a set of screens.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Modelling the Workflow</SectionHeadline>
                    <Para>
                        Rather than treating each screen as an independent design, we broke the
                        product into reusable application patterns. A support interaction could be
                        represented as one sequence:
                    </Para>
                    <Model>
                        Customer Context &rarr; Issue &rarr; Relevant Data &rarr; Recommended Action
                        &rarr; Resolution
                    </Model>
                    <Para>
                        That model let us treat the application as a system rather than a collection
                        of screens. New workflows could reuse existing behavior instead of requiring
                        a new interface.
                    </Para>
                    <DiagramSlot>
                        <AgentWorkflow />
                    </DiagramSlot>
                    <Para>
                        I defined reusable components and interaction patterns across three areas:{" "}
                        <strong>customer context</strong> (account information, service status,
                        equipment, prior interactions), <strong>troubleshooting</strong> (diagnostics,
                        issue states, recommended actions, escalation paths), and{" "}
                        <strong>application state</strong> (loading, success, warning, error,
                        unavailable data, permissions, empty).
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Component Architecture</SectionHeadline>
                    <Para>
                        The important engineering decision was separating product logic from how
                        information gets presented. Instead of one-off components per screen, we
                        established reusable patterns with defined states, variants, interaction
                        behavior, accessibility requirements, responsive behavior, content
                        constraints, and data requirements.
                    </Para>
                    <DiagramSlot>
                        <AppArchitecture />
                    </DiagramSlot>
                    <Para>
                        That created a shared contract between product, design, and engineering. The
                        design system was not a Figma library &mdash; it was{" "}
                        <strong>part of the frontend architecture of the product</strong>.
                    </Para>
                    <DiagramSlot>
                        <ComponentAnatomy />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Building for Real Application States</SectionHeadline>
                    <Para>
                        Enterprise applications rarely exist in the state shown in a mockup. Customer
                        data could be incomplete. Services could fail. Information could take time to
                        load. Agents hit accounts with completely different configurations.
                    </Para>
                    <Para>
                        So components and workflows were built around state rather than screenshots.
                        Thinking that way kept the frontend predictable even when the data
                        underneath it was not.
                    </Para>
                    <DiagramSlot>
                        <StateCoverage />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Accessibility as a Constraint</SectionHeadline>
                    <Para>
                        Accessibility was treated as part of component behavior, not something added
                        after implementation. Reusable patterns accounted for keyboard navigation,
                        focus management, semantic structure, accessible labels, error
                        communication, interaction states, and contrast.
                    </Para>
                    <Para>
                        Defining that at the component level meant an accessibility improvement
                        propagated across every workflow at once, instead of being solved again on
                        each screen.
                    </Para>

                    <SubHead accentColor={colors.cyan[300]}>Validating With Real Agents</SubHead>
                    <Para>
                        We tested workflows directly with support agents &mdash; not asking whether
                        they liked an interface, but watching them move through real scenarios. Where
                        did they hesitate? What did they search for? When did they leave WIN and open
                        another system? What did they need before they could decide?
                    </Para>
                    <Para>
                        Those observations became product and engineering requirements. Then we
                        iterated and watched again.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Impact</SectionHeadline>
                    <Para>
                        WIN cut the searching and context switching required during a customer
                        interaction.
                    </Para>
                    <MetricsCard
                        metrics={[
                            {
                                value: "5min → 3min",
                                label: "Average Call Handling",
                                description: "Roughly 40% less time per interaction",
                                color: colors.emerald[500],
                            },
                            {
                                value: "100K+",
                                label: "Employees Supported",
                                description: "Deployed across nationwide support operations",
                                color: colors.cyan[300],
                            },
                            {
                                value: "YoY Funding",
                                label: "Reinvestment",
                                description: "Expanded into additional Spectrum workflows",
                                color: colors.amber[400],
                            },
                        ]}
                    />
                    <Para>
                        More importantly, the platform showed enough operational value to earn
                        further investment and expand into other Spectrum workflows.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Retrospective</SectionHeadline>
                    <Para>
                        WIN changed how I think about building software. The hardest problems were
                        not purely design problems or purely engineering problems &mdash; they lived
                        between the two.
                    </Para>
                    <Para>
                        Understanding the user workflow determined the architecture. Understanding
                        the architecture changed which experiences were practical to build at all.
                        That intersection is the foundation of how I approach product engineering
                        now.
                    </Para>
                </Section>
            </Body>
        </Layout>

        <TestimonialSection
            title="What a Colleague Said"
            testimonials={[
                {
                    name: "Todd Roadarmel",
                    title: "Director of Digital Products",
                    body: "Working with Nicholas at Spectrum was an outstanding experience. He consistently brought thoughtful UX solutions to the table and had a unique ability to turn complex workflows into clean, intuitive designs. Nicholas played a key role in developing the WIN tool's design system, and his collaboration with both product and engineering helped move projects forward efficiently. His understanding of front-end development allowed him to make more informed UX decisions—designing with technical feasibility and implementation in mind, which resulted in solutions that were both user-friendly and practical to build. This combination of design thinking and technical awareness made him an invaluable asset to our team.",
                },
            ]}
        />

        <NDADisclosure />
    </PageWrapper>
);

export default Spectrum;
